// src/components/dashboard/UnitEdit/UnitLeaseTerms/UnitLeaseTerms.tsx
import React from 'react';
import { Formik, Form, Field } from 'formik';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import UICollapsibleSection from '@/components/shared/UICollapsibleSection';
import Input from '@/components/shared/Input';
import Button from '@/components/shared/Button';
import './UnitLeaseTerms.scss';
import { ILease } from '@/types';

export interface UnitLeaseTermsData {
  lease: ILease;
}
export interface UnitLeaseTermsProps {
  /**
   * Дані секції
   */
  data: UnitLeaseTermsData;

  /**
   * Callback для збереження даних
   */
  onSave: (data: Partial<UnitLeaseTermsData>) => Promise<void>;

  /**
   * Стан завантаження
   */
  loading?: boolean;

  /**
   * Контрольований стан згорнутості
   */
  collapsed?: boolean;

  /**
   * Обробник зміни стану згорнутості
   */
  onToggleCollapse?: (collapsed: boolean) => void;

  /**
   * Додатковий CSS клас
   */
  className?: string;
}

const baseClass = 'UnitLeaseTerms';

/**
 * Компонент для відображення та редагування умов оренди юніта
 */
const UnitLeaseTerms: React.FC<UnitLeaseTermsProps> = ({
                                                         data,
                                                         onSave,
                                                         loading = false,
                                                         collapsed,
                                                         onToggleCollapse,
                                                         className = '',
                                                       }) => {
  const handleSubmit = async (values: UnitLeaseTermsData) => {
    await onSave(values);
  };

  const handleClear = (resetForm: () => void) => {
    resetForm();
  };

  return (
    <UICollapsibleSection
      title="Terms of lease"
      description=""
      collapsible={true}
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
      loading={loading}
      loadingText="Loading lease terms..."
      className={`${baseClass} ${className}`.trim()}
    >
      <Formik
        enableReinitialize={true}
        initialValues={data}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, errors, touched, handleChange, handleBlur, resetForm }) => (
          <Form className={`${baseClass}__form`}>
            {/* Lease type radio buttons */}
            <div className={`${baseClass}__radio-group`}>
              <div className={`${baseClass}__field-label`}>Lease type</div>
              <div className={`${baseClass}__radio-options`}>
                <label className={`${baseClass}__radio-label`}>
                  <Field
                    className={`${baseClass}__radio-input`}
                    type="radio"
                    name="lease.isFixed"
                    value="fixed"
                    onChange={() => setFieldValue('lease.isFixed', 'fixed')}
                  />
                  Fixed term
                </label>
                <label className={`${baseClass}__radio-label`}>
                  <Field
                    className={`${baseClass}__radio-input`}
                    type="radio"
                    name="lease.isFixed"
                    value="indeterminate"
                    onChange={() => setFieldValue('lease.isFixed', 'indeterminate')}
                  />
                  Indeterminate term
                </label>
              </div>
            </div>

            {/* Fixed term fields */}
            {(values.lease.isFixed === 'fixed' || values.lease.isFixed === true) && (
              <>
                <div className={`${baseClass}__field-group`}>
                  <div className={`${baseClass}__field-label`}>Period (months)</div>
                  <Input
                    className={`${baseClass}__field-input`}
                    onChange={handleChange}
                    type="number"
                    onBlur={handleBlur}
                    placeholder="Period"
                    value={values.lease.period ?? ''}
                    name="lease.period"
                    errors={
                      errors.lease?.period && touched.lease?.period
                        ? errors.lease.period
                        : ''
                    }
                  />
                </div>

                <div className={`${baseClass}__field-group`}>
                  <div className={`${baseClass}__field-label`}>Start date</div>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      className={`${baseClass}__date-picker`}
                      slotProps={{
                        textField: {
                          placeholder: 'Start date',
                        },
                      }}
                      value={values.lease.startDate ? moment(values.lease.startDate) : null}
                      onChange={(newValue) => {
                        setFieldValue('lease.startDate', newValue ? newValue.toDate() : null);
                      }}
                    />
                  </LocalizationProvider>
                </div>

                <div className={`${baseClass}__field-group`}>
                  <div className={`${baseClass}__field-label`}>End date</div>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      className={`${baseClass}__date-picker`}
                      slotProps={{
                        textField: {
                          placeholder: 'End date',
                        },
                      }}
                      value={values.lease.endDate ? moment(values.lease.endDate) : null}
                      onChange={(newValue) => {
                        setFieldValue('lease.endDate', newValue ? newValue.toDate() : null);
                      }}
                    />
                  </LocalizationProvider>
                </div>
              </>
            )}

            {/* Indeterminate term fields */}
            {(values.lease.isFixed === 'indeterminate' || values.lease.isFixed === false) && (
              <div className={`${baseClass}__field-group`}>
                <div className={`${baseClass}__field-label`}>Start date</div>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    className={`${baseClass}__date-picker`}
                    slotProps={{
                      textField: {
                        placeholder: 'Start date',
                      },
                    }}
                    value={
                      values.lease.indeterminateStartDate
                        ? moment(values.lease.indeterminateStartDate)
                        : null
                    }
                    onChange={(newValue) => {
                      setFieldValue(
                        'lease.indeterminateStartDate',
                        newValue ? newValue.toDate() : null
                      );
                    }}
                  />
                </LocalizationProvider>
              </div>
            )}

            {/* Action buttons */}
            <div className={`${baseClass}__actions`}>
              <Button type="submit" theme="primary">
                Save
              </Button>
              <Button
                type="button"
                theme="secondary"
                onClick={() => handleClear(resetForm)}
              >
                Clear
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </UICollapsibleSection>
  );
};

export default UnitLeaseTerms;